import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../backlog/backlog.css';
import '../../modules/module.css';
import '../../modules/SettingsDetailPage.css';
import '../../maintenance/maintenance.css';
import { SrCard, SrColumns } from '../../maintenance/srLayout';
import './maintenanceQueue.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useConfirm, useSaveFlow } from '../../../components/confirm';
import { serverMessage } from '../../../components/confirm/serverMessage';
import { useHotkeys, type HotkeyDef } from '../../../components/hotkeys';
import { BusyIcon, runLoading } from '../../../components/loading';
import { useNotify } from '../../../components/notifications';
import { ViewToggle } from '../../../components/ViewToggle';
import { confirmDiscardIfUnsaved, useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { fmtLocalDate } from '../../../utils/datetime';
import {
  fetchRecord, saveRecord, submitRecord,
  type RecordInput, type RecordTask, type RecordView,
} from './api';
import MaintenanceAttachments from './MaintenanceAttachments';

// ─────────────────────────────────────────────────────────────────────────────
// Instrument Management ▸ Maintenance Queue ▸ Maintenance Record (nav `instm-maint-queue`).
// Port of legacy ALPACA `MaintenanceTaskCheckList_DetailView_MaintenanceQueue` and the actions of
// `LabwareMaintenanceViewController` (Save :631, Submit :732). Opened from the green Start icon on a queue row.
//
// LAYOUT (G5): a two-column header — Maintenance Id · Instrument Name · Maintained Date · Maintained By · Frequency ·
// Next Maintain Date | Task Ownership · Service Company · Serviced By · Skip (+ its reason) · Status — then the
// "Task Details" grid, then "Attachment". Action bar: Refresh · Submit | Save · Save and Close · Cancel, top and bottom.
//
// RULES live on the server (Application MaintenanceRecordRules) and its messages are G5's own; this page only collects
// the ticks and the two editable cells (Action Taken, Comment) and shows what the server says.
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Maintenance Queue';

interface Props {
  oid: string;
  /** Show exactly this set of tasks (what a Submit returned for the ones due next); omitted = the task's own MaintenanceId. */
  onlyOids?: string[];
  onHome: () => void;
  /** Back to the queue list (reloaded). */
  onClose: () => void;
  /** Open another task's record (after a Submit that left more due). */
  onOpen: (oid: string, onlyOids: string[]) => void;
  /** Write access (MAC): Save / Submit are greyed without it. */
  canWrite: boolean;
  /** The queue rows in list order — what the prev / next arrows walk. */
  siblingIds: string[];
  /** Open the record of a neighbouring queue row. */
  onNavigate: (oid: string) => void;
}

/** `yyyy-MM-ddTHH:mm:ss` in the browser's wall clock — the naive shape the server sends dates in. */
function nowNaive(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export default function MaintenanceRecordPage({ oid, onlyOids, onHome, onClose, onOpen, canWrite, siblingIds, onNavigate }: Props) {
  const [tab, setTab] = useState<'Task Details' | 'Attachment'>('Task Details');
  const at = siblingIds.indexOf(oid);
  const prevOid = at > 0 ? siblingIds[at - 1] ?? null : null;
  const nextOid = at >= 0 && at < siblingIds.length - 1 ? siblingIds[at + 1] ?? null : null;
  const notify = useNotify();
  const { confirm } = useConfirm();
  const runSave = useSaveFlow();
  const [view, setView] = useState<RecordView | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  // Header edits
  const [company, setCompany] = useState('');
  const [skip, setSkip] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  // Grid state: the two editable cells per task, and which are ticked (with who / when, as G5 stamps on a tick).
  const [rows, setRows] = useState<RecordTask[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const v = await fetchRecord(oid, onlyOids);
      setView(v);
      setCompany(v.header.serviceCompanyId ?? '');
      setSkip(v.header.skip);
      setSkipReason(v.header.skipReasonId ?? '');
      setRows(v.tasks);
      // A task already ticked and saved comes back with Completed By set — keep it ticked.
      setSelected(new Set(v.tasks.filter((t) => !!t.completedBy).map((t) => t.oid)));
      setDirty(false);
    } catch (e) {
      notify.error(serverMessage(e) ?? 'Failed to open the maintenance record.');
      onClose();
    } finally {
      setLoading(false);
    }
    // onClose / notify are stable enough; reloading on their identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oid, onlyOids?.join(',')]);

  useEffect(() => { void load(); }, [load]);
  useUnsavedGuard(dirty);

  const header = view?.header;

  function edit(taskOid: string, patch: Partial<Pick<RecordTask, 'actionDescription' | 'comment'>>) {
    setRows((rs) => rs.map((r) => (r.oid === taskOid ? { ...r, ...patch } : r)));
    setDirty(true);
  }

  /** A tick stamps Completed By / Date; an untick clears them (G5 ProcessAction :1876-1885). */
  function onSelect(ids: Set<string | number>) {
    setSelected(ids);
    setRows((rs) => rs.map((r) => {
      const on = ids.has(r.oid);
      if (on && !r.completedBy) return { ...r, completedBy: header?.maintainedBy ?? '', completedDate: nowNaive() };
      if (!on && r.completedBy) return { ...r, completedBy: null, completedDate: null };
      return r;
    }));
    setDirty(true);
  }

  const input = (): RecordInput => ({
    openedOid: oid,
    serviceCompanyId: company || null,
    servicedBy: null,
    skip,
    skipReasonId: skip ? skipReason || null : null,
    rows: rows.map((r) => ({
      oid: r.oid, selected: selected.has(r.oid),
      actionDescription: r.actionDescription, comment: r.comment,
    })),
  });

  async function doSave(close: boolean) {
    if (busyRef.current || loading) return;
    busyRef.current = true; setBusy(true);
    try {
      await runSave({
        formName: FORM_NAME,
        auditAction: 'edit',
        save: async () => { await saveRecord(oid, input()); },
        onSaved: () => { if (close) onClose(); else void load(); },
      });
    } finally { busyRef.current = false; setBusy(false); }
  }

  async function doSubmit() {
    if (busyRef.current || loading) return;
    busyRef.current = true; setBusy(true);
    try {
      const ok = await confirm({ title: 'Submit', message: 'Do you want to Submit the selected tasks?' });
      if (!ok) return;
      try {
        const r = await runLoading(submitRecord(oid, input()), { blocking: true, label: 'Submitting…' });
        setDirty(false);
        notify.success('Selected maintenance tasks updated successfully.');
        // More due for this setup → straight to the next; nothing left → back to the queue (G5 :883-889).
        const next = r.nextOids[0];
        if (next) onOpen(next, r.nextOids);
        else onClose();
      } catch (e) {
        notify.warning(serverMessage(e) ?? 'Submit failed.');
      }
    } finally { busyRef.current = false; setBusy(false); }
  }

  const leave = (go: () => void) => () => { void confirmDiscardIfUnsaved().then((okay) => { if (okay) go(); }); };
  const off = busy || loading;
  const keys: HotkeyDef[] = [
    { combo: 'mod+s', label: 'Save', group: 'Detail', order: 1, disabled: off || !canWrite, run: () => { void doSave(false); } },
    { combo: 'mod+enter', label: 'Save and Close', group: 'Detail', order: 3, disabled: off || !canWrite, run: () => { void doSave(true); } },
    { combo: 'alt+u', label: 'Submit', group: 'Detail', order: 4, disabled: off || !canWrite, run: () => { void doSubmit(); } },
    { combo: 'alt+r', label: 'Refresh', group: 'Detail', order: 5, disabled: off, run: leave(() => void load()) },
    { combo: 'esc', label: 'Cancel', group: 'Detail', order: 9, disabled: busy, allowInInput: false, run: leave(onClose) },
    { combo: 'alt+left', label: 'Previous record', group: 'Detail', order: 7, disabled: busy || !prevOid,
      run: () => { if (prevOid) leave(() => onNavigate(prevOid))(); } },
    { combo: 'alt+right', label: 'Next record', group: 'Detail', order: 8, disabled: busy || !nextOid,
      run: () => { if (nextOid) leave(() => onNavigate(nextOid))(); } },
  ];
  useHotkeys(keys);

  const taskColumns: AppColumn<RecordTask>[] = useMemo(() => [
    { field: 'dueDate', header: 'DueDate', width: 100, type: 'date' },
    { field: 'taskId', header: 'TaskID', width: 80 },
    { field: 'category', header: 'Category', width: 140 },
    { field: 'taskDescription', header: 'Task/Description', width: 220 },
    { field: 'actionDescription', header: 'Action Taken', width: 260, sortable: false, filterable: false,
      render: (r) => (
        <input className="mq-cell" value={r.actionDescription ?? ''} disabled={!canWrite}
               aria-label={`Action taken for ${r.taskDescription ?? r.taskId ?? 'task'}`}
               onChange={(e) => edit(r.oid, { actionDescription: e.target.value })} />
      ) },
    { field: 'comment', header: 'Comment', width: 220, sortable: false, filterable: false,
      render: (r) => (
        <input className="mq-cell" value={r.comment ?? ''} disabled={!canWrite}
               aria-label={`Comment for ${r.taskDescription ?? r.taskId ?? 'task'}`}
               onChange={(e) => edit(r.oid, { comment: e.target.value })} />
      ) },
    { field: 'completedDate', header: 'Date', width: 130, type: 'datetime' },
    { field: 'completedBy', header: 'CompletedBy', width: 150 },
  ], [canWrite]);

  // Same bar as every Maintenance full page (MaintenanceDetailFrame): ONE bar at the TOP only, the form's record actions on
  // the LEFT (Refresh, Submit), the Save set on the RIGHT (Save, Save and Close, Cancel), all `ca-btn`.
  const bar = () => (
    <div className="sd-actionbar">
      <div className="maint-bar-left">
        <button className="ca-btn" onClick={leave(() => void load())} disabled={off} title="Refresh (Alt+R)">
          <span className="tb-i tb-i-green">🔄</span> Refresh
        </button>
        <button className="ca-btn" onClick={() => void doSubmit()} disabled={off || !canWrite} title="Submit (Alt+U)">
          <span className="tb-i tb-i-green">✔</span> Submit
        </button>
      </div>
      <button className="ca-btn ca-btn-solid" onClick={() => void doSave(false)} disabled={off || !canWrite} title="Save (Ctrl+S)">
        {busy ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
      </button>
      <button className="ca-btn" onClick={() => void doSave(true)} disabled={off || !canWrite} title="Save and Close (Ctrl+Enter)">
        <span className="tb-i">💾</span> Save and Close
      </button>
      <button className="ca-btn" onClick={leave(onClose)} disabled={busy} title="Cancel (Esc)">
        <span className="tb-i">↩︎</span> Cancel
      </button>
    </div>
  );

  const title = header?.taskDescription ?? '';

  // A read-only field: a label and a greyed input as DIRECT children of the .sr-fgrid column (Sample Registration layout).
  const ro = (label: string, value: string | null | undefined) => (
    <>
      <label>{label}</label>
      <input type="text" readOnly tabIndex={-1} value={value ?? ''} aria-label={label} />
    </>
  );

  return (
    <div className="bl-page mlv-host maint-detail">
      <div className="bl-grid-area">
        {/* The List View / Detail View switch above the card, as on every Maintenance page. */}
        <div className="maint-viewtoggle">
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') leave(onClose)(); }} />
        </div>
        <div className="sd-wrap">
          <div className="sd-head">
            <div>
              <h2>Maintenance Record – {title}</h2>
              <div className="sd-bc">
                <a onClick={leave(onHome)}>Home</a>
                <i className="ti ti-chevron-right" />
                <span>Instrument Management</span>
                <i className="ti ti-chevron-right" />
                <a onClick={leave(onClose)}>Maintenance Queue</a>
                <i className="ti ti-chevron-right" />
                <span>{title}</span>
              </div>
            </div>
            {/* The same GoLive record-nav icons Sample Registration uses (sr-navimg); they walk the queue rows. */}
            <div className="maint-nav">
              <button className="sr-navimg" title="Previous record (Alt+←)" disabled={!prevOid || busy}
                      onClick={() => prevOid && leave(() => onNavigate(prevOid))()}>
                <img src="/nav/sr-nav-back.png" alt="Previous record" />
              </button>
              <button className="sr-navimg sr-navimg-next" title="Next record (Alt+→)" disabled={!nextOid || busy}
                      onClick={() => nextOid && leave(() => onNavigate(nextOid))()}>
                <img src="/nav/sr-nav-fwd.png" alt="Next record" />
              </button>
            </div>
          </div>
          {bar()}

          {header && (
            <div className="maint-detail-body">
              <SrCard title="General Information" icon="🔧">
                <SrColumns>
                  <div className="sr-fgrid maint-fg2">
                    {ro('Maintenance Id', header.maintenanceId)}
                    {ro('Instrument Name', header.instrumentName)}
                    {ro('Maintained Date', fmtLocalDate(header.maintainedDate))}
                    {ro('Maintained By', header.maintainedBy)}
                    {ro('Frequency', header.frequency)}
                    {ro('Next Maintain Date', fmtLocalDate(header.nextMaintainDate))}
                  </div>
                  <div className="sr-fgrid maint-fg2">
                    {ro('Task Ownership', header.taskOwnership)}
                    <label htmlFor="mq-company">Service Company</label>
                    <select id="mq-company" value={company} disabled={!canWrite}
                            onChange={(e) => { setCompany(e.target.value); setDirty(true); }}>
                      <option value="" />
                      {view!.serviceCompanies.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                    {/* NOT editable here. On this database ServicedBy is a GUID column but G5 types it as text, and G5
                        cannot read a GUID into it - one saved value made G5 fail on every page after login (2026-11-02).
                        Until the column and G5 agree, G6 never writes it. */}
                    {ro('Serviced By', '')}
                    <label htmlFor="mq-skip">Skip</label>
                    <input id="mq-skip" type="checkbox" checked={skip} disabled={!canWrite} style={{ width: 'auto', justifySelf: 'start' }}
                           onChange={(e) => { setSkip(e.target.checked); if (!e.target.checked) setSkipReason(''); setDirty(true); }} />
                    {skip && (
                      <>
                        <label htmlFor="mq-skipreason">Skip Reason</label>
                        <select id="mq-skipreason" value={skipReason} disabled={!canWrite}
                                onChange={(e) => { setSkipReason(e.target.value); setDirty(true); }}>
                          <option value="" />
                          {view!.skipReasons.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </select>
                      </>
                    )}
                    {ro('Status', header.status)}
                  </div>
                </SrColumns>
              </SrCard>

              {/* The two collections behind the Sample Registration tab strip; both stay mounted so switching keeps each
                  grid's ticks and edits. */}
              <section className="sr-sec">
                <div className="sr-tabbar" role="tablist">
                  {(['Task Details', 'Attachment'] as const).map((t) => (
                    <button key={t} type="button" role="tab" aria-selected={tab === t}
                            className={`sr-tb${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>{t}</button>
                  ))}
                </div>
                <div className="sr-sec-b">
                  <div style={tab === 'Task Details' ? undefined : { display: 'none' }}>
                    <AppDataGrid<RecordTask>
                      rows={rows}
                      columns={taskColumns}
                      getRowId={(r) => r.oid}
                      loading={loading}
                      selectable
                      selectedIds={selected}
                      onSelectionChange={onSelect}
                      defaultPageSize={20}
                      exportFilename="maintenance-task-details"
                      emptyText="No tasks to do."
                    />
                  </div>
                  <div style={tab === 'Attachment' ? undefined : { display: 'none' }}>
                    <MaintenanceAttachments taskOid={oid} initial={view!.attachments} canWrite={canWrite}
                      loading={loading} exportName="maintenance-attachments" />
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
